// components/auth/ProtectedRoute.tsx
// Guards authenticated routes. Redirects unauthenticated users to /login.
// Updated: also redirects users with invitationStatus 'pending' to /reset-password
//   so that invited users cannot access the app until they have set their password.
// Updated: passes current path as ?redirect= query param to /login so the login
//   page can redirect back to the intended destination after successful sign-in.
// Updated May 2026: mounts useSessionTracker to fire session_started events using
//   the 60-minute inactivity gap rule (visibility/focus events included).
// Updated Jun 2026: skips useSessionTracker when an admin is in impersonation mode
//   to avoid polluting analytics with fake session events for the impersonated user.

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import LoadingScreen from '../ui/LoadingScreen';
import { useSessionTracker } from '../../hooks/useSessionTracker';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function SessionTracker({ userId }: { userId: string }) {
  useSessionTracker(userId);
  return null;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading, userProfile } = useAuth();
  const { isImpersonating } = useImpersonation();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    const redirectParam = encodeURIComponent(location.pathname + location.search + location.hash);
    return <Navigate to={`/login?redirect=${redirectParam}`} replace />;
  }

  if (userProfile?.invitationStatus === 'pending') {
    return <Navigate to="/reset-password" replace />;
  }

  return (
    <>
      {!isImpersonating && <SessionTracker userId={user.id} />}
      {children}
    </>
  );
}
